import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Plus, Calendar, User, FileText, Image as ImageIcon, Search, Filter, MapPin, Box, Users, Phone } from 'lucide-react';
import { CungVeDichAPI } from '../services/API';
import ProvincesService from '../services/ProvincesService';
import SystemHeader from './SystemHeader';

const ShipmentReportList = ({ userInfo, accessToken, onLogout, totalPoints = 0 }) => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 12,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState({
    status: undefined,
    textSearch: ''
  });
  const [provinceNamesMap, setProvinceNamesMap] = useState({});

  // Load province names for all reports
  const loadProvinceNames = useCallback(async (reports) => {
    try {
      const uniqueCityIds = [...new Set(reports.map(r => r.city).filter(Boolean))];
      if (uniqueCityIds.length === 0) return;

      const provinces = await ProvincesService.getProvinces();
      const namesMap = {};
      
      uniqueCityIds.forEach(cityId => {
        const province = provinces.find(p => p.code === cityId);
        if (province) {
          namesMap[cityId] = province.name;
        }
      });
      
      setProvinceNamesMap(namesMap);
    } catch (error) {
      console.error('Error loading province names:', error);
    }
  }, []);

  const getCityName = (cityId) => {
    return provinceNamesMap[cityId] || cityId || '';
  };

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      if (!accessToken) {
        throw new Error('Không có token xác thực');
      }
      
      const result = await CungVeDichAPI.getShipmentReports({
        page: pagination.page,
        pageSize: pagination.pageSize,
        status: filters.status,
        textSearch: filters.textSearch
      }, accessToken);
      
      if (result.success) {
        setReports(result.rows);
        setPagination(prev => ({
          ...prev,
          total: result.count,
          totalPages: result.totalPages
        }));
        
        // Load province names for all reports
        if (result.rows && result.rows.length > 0) {
          await loadProvinceNames(result.rows);
        }
      } else {
        throw new Error('Không thể lấy danh sách báo cáo');
      }
    } catch (error) {
      console.error('Error fetching shipment reports:', error);
      setError(error.message || 'Có lỗi xảy ra khi tải danh sách báo cáo');
    } finally {
      setLoading(false);
    }
  }, [accessToken, pagination.page, pagination.pageSize, filters, loadProvinceNames]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const getStatusBadge = (status) => {
    const statusMap = {
      1: { text: 'Chờ xử lý', bgColor: 'bg-yellow-100', textColor: 'text-yellow-800', borderColor: 'border-yellow-300' },
      2: { text: 'Đã xử lý', bgColor: 'bg-green-100', textColor: 'text-green-800', borderColor: 'border-green-300' },
      3: { text: 'Đã hủy', bgColor: 'bg-red-100', textColor: 'text-red-800', borderColor: 'border-red-300' }
    };
    return statusMap[status] || { text: 'Không xác định', bgColor: 'bg-gray-100', textColor: 'text-gray-800', borderColor: 'border-gray-300' };
  };

  const handleCardClick = (reportId) => {
    const urlParams = new URLSearchParams(window.location.search);
    navigate(`/shipment-report-detail?reportId=${reportId}&${urlParams.toString()}`);
  };

  const handleCreateNew = () => {
    const urlParams = new URLSearchParams(window.location.search);
    navigate(`/shipment-report-form?${urlParams.toString()}`);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to page 1 when filter changes
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SystemHeader 
        userInfo={userInfo}
        onLogout={onLogout}
        totalPoints={totalPoints}
      />
      
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Compact Header & Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            {/* Title */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="p-2 bg-blue-500 rounded-lg">
                <Package className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-lg font-bold text-gray-900">Trả Hàng</h1>
            </div>
            
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={filters.textSearch}
                  onChange={(e) => handleFilterChange('textSearch', e.target.value)}
                  placeholder="Tìm kiếm theo ghi chú..."
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            {/* Status Filter */}
            <div className="w-full md:w-48">
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select
                  value={filters.status || ''}
                  onChange={(e) => handleFilterChange('status', e.target.value ? parseInt(e.target.value) : undefined)}
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                >
                  <option value="">Tất cả</option>
                  <option value="1">⏳ Chờ xử lý</option>
                  <option value="2">✅ Đã xử lý</option>
                  <option value="3">❌ Đã hủy</option>
                </select>
              </div>
            </div>

            {/* Create Button */}
            <button
              onClick={handleCreateNew}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium whitespace-nowrap"
            >
              <Plus className="w-4 h-4" />
              <span>Tạo phiếu trả hàng</span>
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="bg-white rounded-lg shadow-sm p-3 animate-pulse">
                <div className="h-5 bg-gray-200 rounded w-2/3 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-full mb-1.5"></div>
                <div className="h-3 bg-gray-200 rounded w-4/5"></div>
              </div>
            ))}
          </div>
        ) : reports.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Chưa có trả hàng nào</h3>
            <p className="text-gray-600">Tạo phiếu trả hàng đầu tiên để bắt đầu quản lý</p>
          </div>
        ) : (
          <>
            {/* Compact Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {reports.map((report) => {
                const status = getStatusBadge(report.status);
                const cityName = getCityName(report.city);
                
                return (
                  <div
                    key={report.id}
                    onClick={() => handleCardClick(report.id)}
                    className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all cursor-pointer border border-gray-200 hover:border-blue-300"
                  >
                    <div className="p-4">
                      {/* Header */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Package className="w-4 h-4 text-blue-500" />
                          <h3 className="font-semibold text-sm text-gray-900">Trả hàng #{report.id}</h3>
                        </div>
                        <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${status.bgColor} ${status.textColor}`}>
                          {status.text}
                        </span>
                      </div>

                      {/* User - Full Name */}
                      <div className="flex items-center gap-2 mb-2 text-sm text-gray-700">
                        <User className="w-4 h-4 flex-shrink-0" />
                        <span className="font-medium">{report.user?.name || report.user?.userName || 'N/A'}</span>
                      </div>

                      {/* Team Leader */}
                      {report.user?.teamLeaderInfo && (
                        <div className="flex items-center gap-2 mb-2 text-xs text-gray-600">
                          <Users className="w-3.5 h-3.5 flex-shrink-0" />
                          <span>Team Thủ lĩnh: {report.user.teamLeaderInfo.facebook || report.user.teamLeaderInfo.name || 'N/A'}</span>
                        </div>
                      )}

                      {/* Phone Number */}
                      {report.user?.phone && (
                        <div className="flex items-center gap-2 mb-2 text-xs text-gray-600">
                          <Phone className="w-3.5 h-3.5 flex-shrink-0" />
                          <span>SĐT: {report.user.phone}</span>
                        </div>
                      )}

                      {/* Date - Full DateTime */}
                      <div className="flex items-center gap-2 mb-3 text-xs text-gray-500">
                        <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                        <span>{new Date(report.createdAt).toLocaleDateString('vi-VN', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}</span>
                      </div>

                      {/* Products List */}
                      <div className="mb-3">
                        <div className="flex items-center gap-1.5 mb-1.5 text-xs font-medium text-gray-700">
                          <Package className="w-3.5 h-3.5" />
                          <span>Sản phẩm ({report.items?.length || 0})</span>
                        </div>
                        <div className="space-y-1 ml-5">
                          {report.items?.slice(0, 2).map((item, index) => (
                            <div key={item.id || index} className="flex items-start gap-1.5 text-xs">
                              <span className="text-blue-600 font-medium mt-0.5">•</span>
                              <div className="flex-1 min-w-0">
                                <span className="text-gray-900">{item.product?.name || 'N/A'}</span>
                                <span className="text-gray-500 ml-1">(SL: {item.quantity})</span>
                              </div>
                            </div>
                          ))}
                          {report.items?.length > 2 && (
                            <div className="text-xs text-gray-500 italic">
                              ... và {report.items.length - 2} sản phẩm khác
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Address - Only City Name */}
                      {cityName && (
                        <div className="flex items-center gap-2 mb-2 text-xs text-gray-600">
                          <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                          <span>{cityName}</span>
                        </div>
                      )}

                      {/* Total Boxes & Shipment Date - 2 fields per row */}
                      <div className="grid grid-cols-2 gap-2 mb-3">
                        {report.totalBoxes > 0 && (
                          <div className="flex items-center gap-1.5 text-xs text-gray-600">
                            <Box className="w-3.5 h-3.5 flex-shrink-0" />
                            <span>{report.totalBoxes} thùng</span>
                          </div>
                        )}
                        {report.shipmentDate && (
                          <div className="flex items-center gap-1.5 text-xs text-gray-600">
                            <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                            <span>{new Date(report.shipmentDate).toLocaleDateString('vi-VN', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })}</span>
                          </div>
                        )}
                      </div>

                      {/* Notes */}
                      {report.notes && (
                        <div className="flex items-start gap-2 mb-3 text-xs text-gray-600">
                          <FileText className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                          <p className="line-clamp-2 leading-relaxed">{report.notes}</p>
                        </div>
                      )}

                      {/* Attachments Thumbnails */}
                      {report.attachmentUrls && report.attachmentUrls.length > 0 && (
                        <div className="pt-2 border-t border-gray-100">
                          <div className="flex items-center gap-1.5 mb-2">
                            <ImageIcon className="w-3.5 h-3.5 text-green-600" />
                            <span className="text-xs font-medium text-green-600">{report.attachmentUrls.length} ảnh đính kèm</span>
                          </div>
                          <div className="flex gap-1.5 overflow-x-auto pb-1">
                            {report.attachmentUrls.slice(0, 4).map((url, index) => (
                              <img
                                key={index}
                                src={url}
                                alt={`Ảnh ${index + 1}`}
                                className="w-12 h-12 object-cover rounded flex-shrink-0 border border-gray-200"
                              />
                            ))}
                            {report.attachmentUrls.length > 4 && (
                              <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded text-xs font-medium text-gray-600 flex-shrink-0 border border-gray-200">
                                +{report.attachmentUrls.length - 4}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="mt-8 flex justify-center items-center gap-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Trước
                </button>
                
                <div className="flex gap-2">
                  {[...Array(pagination.totalPages)].map((_, index) => {
                    const pageNum = index + 1;
                    // Show first page, last page, current page, and pages around current
                    if (
                      pageNum === 1 ||
                      pageNum === pagination.totalPages ||
                      (pageNum >= pagination.page - 1 && pageNum <= pagination.page + 1)
                    ) {
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`px-4 py-2 rounded-lg ${
                            pageNum === pagination.page
                              ? 'bg-blue-500 text-white'
                              : 'border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    } else if (
                      pageNum === pagination.page - 2 ||
                      pageNum === pagination.page + 2
                    ) {
                      return <span key={pageNum} className="px-2">...</span>;
                    }
                    return null;
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Sau
                </button>
              </div>
            )}

            {/* Results Info */}
            <div className="mt-4 text-center text-sm text-gray-600">
              Hiển thị {((pagination.page - 1) * pagination.pageSize) + 1} - {Math.min(pagination.page * pagination.pageSize, pagination.total)} trong tổng số {pagination.total} phiếu trả hàng
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ShipmentReportList;

